// src/pages/StudentProjectPortal.js - Redesigned with Company Management Layout Pattern
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import ResourceManager from '../components/ResourceManager';
import SubmissionManager from '../components/SubmissionManager';
import StudentRatingDisplay from '../components/StudentRatingDisplay';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { subscribeToConversation, sendMessage, markMessageAsRead, markConversationAsRead } from '../services/messageService';

const StudentProjectPortal = () => {
  const { projectId } = useParams();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // State management
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [activeDeliverable, setActiveDeliverable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activities, setActivities] = useState([]);
  const [resources, setResources] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [previewResource, setPreviewResource] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Fetch all project data
  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        setLoading(true);
        
        // Get project data
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (!projectDoc.exists()) {
          throw new Error('Project not found');
        }
        
        const projectData = { id: projectDoc.id, ...projectDoc.data() };
        setProject(projectData);
        
        // Get company data
        const companyDoc = await getDoc(doc(db, 'companyProfiles', projectData.companyId));
        if (companyDoc.exists()) {
          setCompany({ id: companyDoc.id, ...companyDoc.data() });
        }
        
        // Get deliverables and sort by due date
        const deliverablesQuery = query(
          collection(db, 'deliverables'),
          where('projectId', '==', projectId)
        );
        
        const deliverablesSnapshot = await getDocs(deliverablesQuery);
        const deliverablesData = deliverablesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setDeliverables(deliverablesData);
        setActiveDeliverable(deliverablesData[0]);
        
        // Get resources
        const resourcesQuery = query(
          collection(db, 'projectResources'),
          where('projectId', '==', projectId)
        );
        
        const resourcesSnapshot = await getDocs(resourcesQuery);
        const resourcesData = resourcesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setResources(resourcesData);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching project data:', error);
        setError(error.message);
        setLoading(false);
      }
    };
    
    if (projectId && currentUser) {
      fetchProjectData();
    }
  }, [projectId, currentUser]);

  // Message handling
  const handleSubmitMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;
    
    try {
      const messageData = {
        projectId,
        content: newMessage,
        senderId: currentUser.uid,
        senderType: 'student',
        recipientId: project.companyId,
        recipientType: 'company',
      };
      
      await sendMessage(messageData);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Subscribe to messages
  useEffect(() => {
    if (!projectId || !currentUser || !project?.companyId) return;

    // Mark existing messages as read
    markConversationAsRead(projectId, currentUser.uid, project.companyId);

    // Subscribe to conversation updates
    const unsubscribe = subscribeToConversation(
      projectId,
      currentUser.uid,
      project.companyId,
      (messages) => {
        setMessages(messages);
        // Mark new messages as read as they come in
        messages
          .filter(msg => !msg.isRead && msg.recipientId === currentUser.uid)
          .forEach(msg => markMessageAsRead(msg.id));
      }
    );

    return () => unsubscribe();
  }, [projectId, currentUser, project?.companyId]);

  // Resource click handler with preview
  const handleResourceClick = (resource) => {
    setPreviewResource(resource);
    setShowPreview(true);
  };

  // Close preview
  const closePreview = () => {
    setShowPreview(false);
    setPreviewResource(null);
  };

  // Direct download
  const handleDirectDownload = (resource, e) => {
    e.stopPropagation();
    if (resource.resourceType === 'link' || resource.url) {
      window.open(resource.url || resource.fileUrl, '_blank');
    } else if (resource.fileUrl) {
      window.open(resource.fileUrl, '_blank');
    }
  };

  // Render preview content
  const renderPreview = () => {
    if (!previewResource) return null;
    
    const { resourceType, fileUrl, fileType, title } = previewResource;
    
    if (resourceType === 'link') {
      return (
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
          <p>External link: <a href={fileUrl} target="_blank" rel="noopener noreferrer">{fileUrl}</a></p>
          <a 
            href={fileUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn btn-primary"
            style={{ marginTop: '20px' }}
          >
            Open Link
          </a>
        </div>
      );
    }
    
    if (!fileType) {
      return (
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
          <p>Preview not available for this file type.</p>
          <a 
            href={fileUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn btn-primary"
            style={{ marginTop: '20px' }}
          >
            Download File
          </a>
        </div>
      );
    }
    
    const type = fileType.toLowerCase();
    
    if (type.includes('pdf')) {
      return (
        <iframe 
          src={`${fileUrl}#toolbar=0&navpanes=0`}
          title={title}
          width="100%"
          height="500px"
          style={{ border: 'none' }}
        />
      );
    }
    
    if (type.includes('image')) {
      return (
        <img 
          src={fileUrl} 
          alt={title} 
          style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
        />
      );
    }
    
    if (type.includes('video')) {
      return (
        <video 
          src={fileUrl} 
          controls 
          style={{ maxWidth: '100%', maxHeight: '500px' }}
        />
      );
    }
    
    if (type.includes('audio')) {
      return (
        <audio 
          src={fileUrl} 
          controls 
          style={{ width: '100%' }}
        />
      );
    }
    
    return (
      <div style={{ textAlign: 'center', padding: '50px 20px' }}>
        <p>Preview not available for this file type.</p>
        <a 
          href={fileUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="btn btn-primary"
          style={{ marginTop: '20px' }}
        >
          Download File
        </a>
      </div>
    );
  };

  // Utility functions
  const calculateProgress = () => {
    if (!deliverables.length) return 0;
    
    const completedCount = deliverables.filter(d => 
      d.status === 'completed' || d.status === 'approved'
    ).length;
    
    return Math.round((completedCount / deliverables.length) * 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date set';
    
    try {
      if (dateStr.seconds) {
        return new Date(dateStr.seconds * 1000).toLocaleDateString();
      }
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return dateStr;
    }
  };

  const getResourceIcon = (resource) => {
    if (!resource) return 'file';
    if (resource.resourceType === 'link') return 'link';
    const fileType = resource.fileType?.toLowerCase() || '';
    if (fileType.includes('pdf')) return 'pdf';
    if (fileType.includes('image')) return 'image';
    if (fileType.includes('video')) return 'video';
    if (fileType.includes('audio')) return 'audio';
    if (fileType.includes('doc')) return 'doc';
    if (fileType.includes('sheet') || fileType.includes('excel')) return 'sheet';
    return 'file';
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'success';
      case 'in-progress':
      case 'submitted':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'danger';
      default:
        return '';
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const due = dueDate.seconds ? new Date(dueDate.seconds * 1000) : new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDeadlineStatus = (dueDate, status) => {
    if (status === 'completed' || status === 'approved') {
      return {
        text: 'Completed',
        style: { color: 'var(--success)' }
      };
    }

    const daysRemaining = getDaysUntilDue(dueDate);
    
    if (daysRemaining < 0) {
      return {
        text: 'Missing',
        style: { color: 'var(--danger)', fontWeight: '600' }
      };
    }
    
    if (daysRemaining === 0) {
      return {
        text: 'Due Today',
        style: { color: 'var(--warning)', fontWeight: '600' }
      };
    }
    
    if (daysRemaining <= 2) {
      return {
        text: `${daysRemaining} days remaining`,
        style: { color: 'var(--danger)' }
      };
    }
    
    if (daysRemaining <= 7) {
      return {
        text: `${daysRemaining} days remaining`,
        style: { color: 'var(--warning)' }
      };
    }
    
    return {
      text: `${daysRemaining} days remaining`,
      style: { color: '#666' }
    };
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <Navigation />
      <div className="container" style={{ padding: '80px 0' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#666' }}>
            Loading project details...
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--danger)' }}>
            {error}
          </div>
        ) : (
          <>
            {/* Header - matching StudentDashboard style */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '40px' 
            }}>
              <div>
                <h1 style={{ 
                  fontSize: '36px', 
                  fontWeight: 'bold', 
                  margin: '0 0 8px 0',
                  color: '#1a1a1a'
                }}>
                  {project?.title}
                </h1>
                <div style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  alignItems: 'center',
                  margin: 0, 
                  color: '#666', 
                  fontSize: '16px'
                }}>
                  <span className="feature-badge">{project?.category}</span>
                  <span>Project Portal</span>
                </div>
              </div>
              <button 
                onClick={() => navigate('/student/dashboard')} 
                className="btn"
                style={{ 
                  backgroundColor: '#6366f1',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Back to Dashboard
              </button>
            </div>

            {/* Main Tab Navigation */}
            <div style={{ 
              background: 'white', 
              borderRadius: '16px', 
              boxShadow: '0 5px 15px rgba(0,0,0,0.05)', 
              overflow: 'hidden', 
              marginBottom: '32px' 
            }}>
              <div style={{ borderBottom: '1px solid #eee', padding: '5px 0' }}>
                <div style={{ display: 'flex' }}>
                  <button 
                    onClick={() => setActiveTab('overview')} 
                    style={{ 
                      flex: '1', 
                      padding: '15px', 
                      background: activeTab === 'overview' ? '#f5f7ff' : 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'overview' ? '3px solid var(--primary)' : 'none',
                      cursor: 'pointer',
                      fontWeight: activeTab === 'overview' ? 'bold' : 'normal',
                      color: activeTab === 'overview' ? 'var(--primary)' : 'inherit'
                    }}
                  >
                    Overview
                  </button>
                  <button 
                    onClick={() => setActiveTab('assignments')} 
                    style={{ 
                      flex: '1', 
                      padding: '15px', 
                      background: activeTab === 'assignments' ? '#f5f7ff' : 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'assignments' ? '3px solid var(--primary)' : 'none',
                      cursor: 'pointer',
                      fontWeight: activeTab === 'assignments' ? 'bold' : 'normal',
                      color: activeTab === 'assignments' ? 'var(--primary)' : 'inherit'
                    }}
                  >
                    Assignments
                  </button>
                  <button 
                    onClick={() => setActiveTab('resources')} 
                    style={{ 
                      flex: '1', 
                      padding: '15px', 
                      background: activeTab === 'resources' ? '#f5f7ff' : 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'resources' ? '3px solid var(--primary)' : 'none',
                      cursor: 'pointer',
                      fontWeight: activeTab === 'resources' ? 'bold' : 'normal',
                      color: activeTab === 'resources' ? 'var(--primary)' : 'inherit'
                    }}
                  >
                    Resources
                  </button>
                  <button 
                    onClick={() => setActiveTab('messages')} 
                    style={{ 
                      flex: '1', 
                      padding: '15px', 
                      background: activeTab === 'messages' ? '#f5f7ff' : 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'messages' ? '3px solid var(--primary)' : 'none',
                      cursor: 'pointer',
                      fontWeight: activeTab === 'messages' ? 'bold' : 'normal',
                      color: activeTab === 'messages' ? 'var(--primary)' : 'inherit'
                    }}
                  >
                    Messages
                  </button>
                  <button 
                    onClick={() => setActiveTab('ratings')} 
                    style={{ 
                      flex: '1', 
                      padding: '15px', 
                      background: activeTab === 'ratings' ? '#f5f7ff' : 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'ratings' ? '3px solid var(--primary)' : 'none',
                      cursor: 'pointer',
                      fontWeight: activeTab === 'ratings' ? 'bold' : 'normal',
                      color: activeTab === 'ratings' ? 'var(--primary)' : 'inherit'
                    }}
                  >
                    Ratings
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div style={{ padding: '32px' }}>
                {/* Overview Tab - SIMPLIFIED */}
                {activeTab === 'overview' && (
                  <div>
                    {/* Progress Overview */}
                    <div style={{ marginBottom: '32px' }}>
                      <h2 style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        margin: '0 0 24px 0',
                        color: '#1a1a1a'
                      }}>
                        Project Progress
                      </h2>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                        <div style={{ 
                          padding: '20px', 
                          background: '#f5f7ff', 
                          borderRadius: '16px', 
                          textAlign: 'center',
                          border: '1px solid #e0f2fe'
                        }}>
                          <h4 style={{ 
                            marginBottom: '10px', 
                            color: 'var(--primary)',
                            fontSize: '16px',
                            fontWeight: 'bold'
                          }}>
                            Overall Progress
                          </h4>
                          <p style={{ fontSize: '32px', fontWeight: '700', margin: '10px 0' }}>{calculateProgress()}%</p>
                          <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div 
                              style={{ 
                                height: '100%', 
                                width: `${calculateProgress()}%`, 
                                background: 'linear-gradient(to right, var(--gradient-start), var(--gradient-end))',
                                borderRadius: '4px',
                                transition: 'width 0.5s ease'
                              }}
                            ></div>
                          </div>
                        </div>

                        <div style={{ 
                          padding: '20px', 
                          background: '#f5f7ff', 
                          borderRadius: '16px', 
                          textAlign: 'center',
                          border: '1px solid #e0f2fe'
                        }}>
                          <h4 style={{ 
                            marginBottom: '10px', 
                            color: 'var(--primary)',
                            fontSize: '16px',
                            fontWeight: 'bold'
                          }}>
                            Assignments
                          </h4>
                          <p style={{ fontSize: '32px', fontWeight: '700', margin: '10px 0' }}>{deliverables.length}</p>
                          <p style={{ color: '#666', fontSize: '14px' }}> In total </p>
                        </div>

                        <div style={{ 
                          padding: '20px', 
                          background: '#f5f7ff', 
                          borderRadius: '16px', 
                          textAlign: 'center',
                          border: '1px solid #e0f2fe'
                        }}>
                          <h4 style={{ 
                            marginBottom: '10px', 
                            color: 'var(--primary)',
                            fontSize: '16px',
                            fontWeight: 'bold'
                          }}>
                            Completed
                          </h4>
                          <p style={{ fontSize: '32px', fontWeight: '700', margin: '10px 0' }}>
                            {deliverables.filter(d => d.status === 'completed' || d.status === 'approved').length}
                          </p>
                          <p style={{ color: '#666', fontSize: '14px' }}>Assignments done</p>
                        </div>
                      </div>
                    </div>

                    {/* Project Description */}
                    <div style={{ 
                      backgroundColor: 'white',
                      padding: '32px',
                      borderRadius: '16px',
                      marginBottom: '32px'
                    }}>
                      <h2 style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        margin: '0 0 16px 0',
                        color: '#1a1a1a'
                      }}>
                        Project Description
                      </h2>
                      <p style={{ 
                        whiteSpace: 'pre-wrap', 
                        lineHeight: '1.6',
                        color: '#666', 
                        fontSize: '16px', 
                        margin: '0 0 20px 0'
                      }}>
                        {project?.description}
                      </p>
                      
                      <div style={{ marginTop: '20px' }}>
                        <h4 style={{ 
                          marginBottom: '10px',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#1a1a1a'
                        }}>
                          Required Skills
                        </h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {project?.skills?.map(skill => (
                            <span key={skill} className="feature-badge">{skill}</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Project Details */}
                    <div style={{ 
                      backgroundColor: 'white',
                      padding: '32px',
                      borderRadius: '16px'
                    }}>
                      <h2 style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        margin: '0 0 24px 0',
                        color: '#1a1a1a'
                      }}>
                        Project Details
                      </h2>
                      <div style={{ 
                        display: 'flex', 
                        gap: '24px', 
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        <div>
                          <span style={{ fontWeight: 'bold' }}>Duration:</span> {project?.duration}
                        </div>
                        <div>
                          <span style={{ fontWeight: 'bold' }}>Compensation:</span> {project?.isExperienceOnly ? 'Experience Only' : `$${project?.compensation}`}
                        </div>
                        <div>
                          <span style={{ fontWeight: 'bold' }}>Company:</span> {company?.companyName || 'Company'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Assignments Tab */}
                {activeTab === 'assignments' && (
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      marginBottom: '24px' 
                    }}>
                      <h2 style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        margin: '0',
                        color: '#1a1a1a'
                      }}>
                        Project Assignments
                      </h2>
                      <span style={{ 
                        color: '#666',
                        fontSize: '14px'
                      }}>
                        {deliverables.length} total
                      </span>
                    </div>

                    {deliverables.length === 0 ? (
                      <div style={{ 
                        backgroundColor: '#f9fafb',
                        padding: '60px 32px',
                        borderRadius: '16px',
                        textAlign: 'center'
                      }}>
                        <p style={{ 
                          color: '#666',
                          fontSize: '16px',
                          margin: 0
                        }}>
                          No assignments have been created yet.
                        </p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {deliverables.map((deliverable, index) => (
                          <div key={deliverable.id} style={{ 
                            backgroundColor: 'white',
                            padding: '32px', 
                            borderRadius: '16px',
                            border: '1px solid #f3f4f6',
                            marginBottom: '0'
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'flex-start', 
                              marginBottom: '16px' 
                            }}>
                              <div>
                                <h3 style={{ 
                                  marginBottom: '8px',
                                  fontSize: '20px',
                                  fontWeight: 'bold',
                                  color: '#1a1a1a'
                                }}>
                                  {deliverable.title}
                                </h3>
                                <div style={{ 
                                  fontSize: '14px', 
                                  color: '#666', 
                                  marginBottom: '0' 
                                }}>
                                  <span style={{ fontWeight: 'bold' }}>Due:</span> {formatDate(deliverable.dueDate)}
                                  {getDaysUntilDue(deliverable.dueDate) !== null && (
                                    <span style={getDeadlineStatus(deliverable.dueDate, deliverable.status).style}>
                                      {' '}({getDeadlineStatus(deliverable.dueDate, deliverable.status).text})
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span 
                                className="feature-badge"
                                style={{
                                  ...getStatusClass(deliverable.status) === 'success' && { 
                                    background: 'rgba(34, 197, 94, 0.1)', 
                                    color: 'var(--success)' 
                                  },
                                  ...getStatusClass(deliverable.status) === 'primary' && { 
                                    background: 'rgba(99, 102, 241, 0.1)', 
                                    color: 'var(--primary)' 
                                  },
                                  ...getStatusClass(deliverable.status) === 'warning' && { 
                                    background: 'rgba(251, 191, 36, 0.1)', 
                                    color: 'var(--warning)' 
                                  },
                                  ...getStatusClass(deliverable.status) === 'danger' && { 
                                    background: 'rgba(239, 68, 68, 0.1)', 
                                    color: 'var(--danger)' 
                                  },
                                  textTransform: 'capitalize'
                                }}
                              >
                                {deliverable.status}
                              </span>
                            </div>
                            
                            <p style={{ 
                              marginBottom: '20px', 
                              lineHeight: '1.6',
                              color: '#666',
                              fontSize: '16px'
                            }}>
                              {deliverable.description}
                            </p>
                            
                            {deliverable === activeDeliverable && 
                             (deliverable.status === 'pending' || deliverable.status === 'in-progress' || deliverable.status === 'rejected') && (
                              <SubmissionManager
                                deliverable={deliverable}
                                projectId={projectId}
                                userType="student"
                                onSubmissionUpdate={() => {
                                  // Refresh deliverables to show updated status
                                  window.location.reload();
                                }}
                              />
                            )}
                            
                            {deliverable.status === 'submitted' && (
                              <div style={{ 
                                marginTop: '15px',
                                padding: '15px',
                                background: 'rgba(108, 99, 255, 0.1)',
                                borderRadius: '8px',
                                color: 'var(--primary)'
                              }}>
                                <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Submitted for Review</p>
                                <p>Your assignment has been submitted and is being reviewed by your mentor.</p>
                              </div>
                            )}
                            
                            {(deliverable.status === 'completed' || deliverable.status === 'approved') && (
                              <div style={{ 
                                marginTop: '15px',
                                padding: '15px',
                                background: 'rgba(34, 197, 94, 0.1)',
                                borderRadius: '8px',
                                color: 'var(--success)'
                              }}>
                                <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Assignment Approved!</p>
                                {deliverable.feedback && (
                                  <div>
                                    <p style={{ fontWeight: 'bold', marginTop: '10px' }}>Feedback:</p>
                                    <p>{deliverable.feedback}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {deliverable !== activeDeliverable && 
                             (deliverable.status === 'pending' || deliverable.status === 'in-progress') && (
                              <button 
                                onClick={() => setActiveDeliverable(deliverable)}
                                className="btn btn-outline"
                                style={{ marginTop: '15px' }}
                              >
                                Work on This Assignment
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Resources Tab */}
                {activeTab === 'resources' && (
                  <div>
                    <ResourceManager projectId={projectId} userType="student" />
                  </div>
                )}

                {/* Messages Tab */}
                {activeTab === 'messages' && (
                  <div>
                    <h2 style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      margin: '0 0 24px 0',
                      color: '#1a1a1a'
                    }}>
                      Messages with {company?.companyName || 'Mentor'}
                    </h2>
                    
                    {messages.length === 0 ? (
                      <div style={{ 
                        backgroundColor: '#f9fafb',
                        padding: '60px 32px',
                        borderRadius: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '18px', marginBottom: '20px', color: '#6b7280', fontWeight: '600' }}>Messages</div>
                        <h3 style={{ 
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color: '#1a1a1a',
                          marginBottom: '12px'
                        }}>
                          No messages yet
                        </h3>
                        <p style={{ 
                          color: '#666',
                          fontSize: '16px',
                          margin: 0
                        }}>
                          Start a conversation with your mentor using the message form below.
                        </p>
                      </div>
                    ) : (
                      <div style={{ 
                        backgroundColor: 'white',
                        padding: '32px',
                        borderRadius: '16px',
                        border: '1px solid #f3f4f6',
                        marginBottom: '24px'
                      }}>
                        <div style={{ 
                          maxHeight: '400px',
                          overflowY: 'auto',
                          marginRight: '-16px',
                          paddingRight: '16px'
                        }}>
                          {messages.map(message => (
                            <div 
                              key={message.id} 
                              style={{ 
                                marginBottom: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: message.senderType === 'student' ? 'flex-end' : 'flex-start'
                              }}
                            >
                              <div style={{ 
                                background: message.senderType === 'student' ? 'var(--primary)' : '#f5f7ff',
                                color: message.senderType === 'student' ? 'white' : '#1a1a1a',
                                padding: '16px 20px',
                                borderRadius: '16px',
                                maxWidth: '70%',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                border: message.senderType === 'student' ? 'none' : '1px solid #e0f2fe'
                              }}>
                                {message.content}
                              </div>
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#666',
                                marginTop: '8px',
                                padding: '0 8px'
                              }}>
                                {message.timestamp?.seconds != null
                                  ? formatDate(message.timestamp) + ' ' + new Date(message.timestamp.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                  : 'Just now'
                                }
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div style={{ 
                      backgroundColor: 'white',
                      padding: '32px',
                      borderRadius: '16px',
                      border: '1px solid #f3f4f6'
                    }}>
                      <h3 style={{ 
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        marginBottom: '16px'
                      }}>
                        Send Message
                      </h3>
                      <form onSubmit={handleSubmitMessage}>
                        <div style={{ marginBottom: '16px' }}>
                          <textarea 
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your message to your mentor..."
                            style={{ 
                              width: '100%',
                              padding: '12px',
                              borderRadius: '8px',
                              border: '1px solid #d1d5db',
                              minHeight: '100px',
                              resize: 'vertical',
                              fontSize: '14px',
                              fontFamily: 'inherit'
                            }}
                          />
                        </div>
                        <button 
                          type="submit"
                          disabled={!newMessage.trim()}
                          style={{
                            backgroundColor: '#6366f1',
                            color: 'white',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: !newMessage.trim() ? 'not-allowed' : 'pointer',
                            opacity: !newMessage.trim() ? 0.6 : 1
                          }}
                        >
                          Send Message
                        </button>
                      </form>
                    </div>
                  </div>
                )}

                {/* Ratings Tab */}
                {activeTab === 'ratings' && (
                  <div>
                    <h2 style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      margin: '0 0 24px 0',
                      color: '#1a1a1a'
                    }}>
                      Your Ratings & Reviews
                    </h2>
                    
                    <div style={{ 
                      marginBottom: '24px',
                      padding: '20px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '12px',
                      border: '1px solid #bbf7d0'
                    }}>
                      <h3 style={{ 
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#15803d',
                        marginBottom: '8px'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', color: '#10b981' }}>
                          <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/>
                          <polyline points="17,6 23,6 23,12"/>
                        </svg>
                        Build Your Professional Reputation
                      </h3>
                      <p style={{ 
                        color: '#166534',
                        fontSize: '14px',
                        margin: 0,
                        lineHeight: 1.5
                      }}>
                        Complete projects successfully to earn positive ratings from companies. 
                        These ratings help showcase your skills to future employers and build your professional reputation on the platform.
                      </p>
                    </div>
                    
                    <StudentRatingDisplay 
                      studentId={currentUser?.uid} 
                      showFullDetails={true}
                      maxReviews={10}
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Resource Preview Modal */}
            {showPreview && previewResource && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  width: '90%',
                  maxWidth: '900px',
                  maxHeight: '80vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{ 
                    padding: '20px', 
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3>{previewResource.title}</h3>
                    <button 
                      onClick={closePreview}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        fontSize: '24px',
                        cursor: 'pointer' 
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{ padding: '20px', overflow: 'auto', flex: 1 }}>
                    {renderPreview()}
                  </div>
                  <div style={{ 
                    padding: '15px 20px', 
                    borderTop: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px'
                  }}>
                    <button 
                      onClick={closePreview}
                      className="btn btn-outline"
                      style={{ padding: '8px 20px' }}
                    >
                      Close
                    </button>
                    <a 
                      href={previewResource.fileUrl || previewResource.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      style={{ padding: '8px 20px' }}
                    >
                      {previewResource.resourceType === 'link' ? 'Open Link' : 'Download'}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentProjectPortal;